import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useLang, ui } from '../i18n/LanguageContext';
import { categories, services } from '../mock/data';
import { PageHero } from './About';
import useReveal from '../hooks/useReveal';

const Services = () => {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const catParam = searchParams.get('cat');
  const [active, setActive] = useState(catParam || 'all');
  useReveal();

  useEffect(() => {
    setActive(catParam || 'all');
  }, [catParam]);

  const filtered = active === 'all' ? services : services.filter((s) => s.category === active);

  const setCat = (slug) => {
    setActive(slug);
    if (slug === 'all') setSearchParams({});
    else setSearchParams({ cat: slug });
  };

  return (
    <div>
      <PageHero
        tag={t(ui.services.tag)}
        title={t(ui.services.title)}
        image="https://images.unsplash.com/photo-1585346077228-9deaf370683a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />

      <section className="py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <p className="text-lg text-muted-foreground max-w-3xl mb-12 leading-relaxed reveal">{t(ui.services.intro)}</p>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-14">
            <button
              onClick={() => setCat('all')}
              className={`px-5 py-2.5 text-sm border transition-all duration-300 ${
                active === 'all' ? 'bg-[#161514] text-white border-[#161514]' : 'border-border hover:border-[#161514]'
              }`}
            >
              {t(ui.cta.allServices)}
            </button>
            {categories.map((c) => (
              c.path ? (
                <Link
                  key={c.slug}
                  to={c.path}
                  className="px-5 py-2.5 text-sm border border-border hover:border-[#161514] transition-all duration-300"
                >
                  {t(c.short)}
                </Link>
              ) : (
                <button
                  key={c.slug}
                  onClick={() => setCat(c.slug)}
                  className={`px-5 py-2.5 text-sm border transition-all duration-300 ${
                    active === c.slug ? 'bg-[#161514] text-white border-[#161514]' : 'border-border hover:border-[#161514]'
                  }`}
                >
                  {t(c.short)}
                </button>
              )
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {active === 'all' && categories.filter((c) => c.path).map((c, i) => (
              <Link key={c.slug} to={c.path} className="group reveal" data-testid={`service-category-card-${c.slug}`}>
                <div className="img-zoom aspect-[4/3] mb-5 bg-secondary">
                  <img src={c.image} alt={t(c.short)} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                </div>
                <p className="eyebrow text-champagne mb-2">{t(c.short)}</p>
                <h3 className="font-serif text-xl mb-2 group-hover:text-champagne transition-colors flex items-start justify-between gap-2">
                  {t(c.title)}
                  <ArrowUpRight size={18} className="shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(c.desc)}</p>
              </Link>
            ))}
            {filtered.map((s, i) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group reveal" style={{ transitionDelay: `${(i % 3) * 90}ms` }}>
                <div className="img-zoom aspect-[4/3] mb-5 bg-secondary">
                  <img src={s.image} alt={t(s.title)} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                </div>
                <p className="eyebrow text-champagne mb-2">{t(categories.find((c) => c.slug === s.category).short)}</p>
                <h3 className="font-serif text-xl mb-2 group-hover:text-champagne transition-colors flex items-start justify-between gap-2">
                  {t(s.title)}
                  <ArrowUpRight size={18} className="shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(s.excerpt)}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
